import { useEffect, useRef } from "react";
import { useContent } from "@/context/ContentContext";
import { useAuth } from "@/context/AuthContext";

// Generic, code-free in-place text editor. When a Super Admin turns on "Edit text",
// clicking ANY text on ANY page makes it editable inline; the change is stored as a
// content override keyed by (route + original text + occurrence) and re-applied on
// every load — no per-string <Txt> wiring needed.
const SEL = "h1,h2,h3,h4,h5,h6,p,span,label,a,button,li,td,th,dt,dd,figcaption,blockquote,strong,em,small";
const norm = (s) => (s || "").replace(/\s+/g, " ").trim();
function hash(s) { let h = 0; for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0; return (h >>> 0).toString(36); }

export default function LiveEdit() {
  const { overrides, editMode, updateString, resetString } = useContent();
  const auth = useAuth();
  const active = editMode && auth?.me?.user?.is_super_admin;
  const applying = useRef(false);

  useEffect(() => {
    if (!active) return;
    const routeKey = () => (window.location.hash || window.location.pathname || "/").split("?")[0];

    const isCandidate = (el) => {
      if (!el || el.childElementCount !== 0) return false;
      if (el.isContentEditable && !el.dataset.liveKey) return false;
      if (el.closest("[data-live-skip]")) return false;
      const t = norm(el.dataset.liveOrig ?? el.textContent);
      if (!t || t.length > 240) return false;
      if (/^[\d\s.,%$/:+×–-]+$/.test(t)) return false; // skip pure numbers / dynamic values
      return true;
    };

    const apply = () => {
      if (applying.current) return;
      applying.current = true;
      try {
        const rk = routeKey();
        const counts = new Map();
        document.querySelectorAll(SEL).forEach((el) => {
          if (!isCandidate(el)) return;
          const orig = norm(el.dataset.liveOrig ?? el.textContent);
          const n = counts.get(orig) || 0; counts.set(orig, n + 1);
          const key = `live:${rk}:${hash(orig)}:${n}`;
          if (!el.dataset.liveOrig) el.dataset.liveOrig = orig;
          el.dataset.liveKey = key;
          el.classList.add("live-editable");
          const ov = overrides[key];
          if (ov != null && norm(el.textContent) !== norm(ov)) el.textContent = ov;
        });
      } finally { applying.current = false; }
    };

    const onClick = (e) => {
      const el = e.target.closest(".live-editable");
      if (!el || !el.dataset.liveKey || el.dataset.editingNow) return;
      e.preventDefault(); e.stopPropagation();
      const key = el.dataset.liveKey;
      const orig = el.dataset.liveOrig || "";
      const before = norm(el.textContent);
      el.dataset.editingNow = "1";
      applying.current = true;
      el.contentEditable = "true";
      el.classList.add("live-editing");
      el.focus();
      const range = document.createRange(); range.selectNodeContents(el);
      const sel = window.getSelection(); sel.removeAllRanges(); sel.addRange(range);
      const finish = (commit) => {
        el.contentEditable = "false";
        el.classList.remove("live-editing");
        delete el.dataset.editingNow;
        el.removeEventListener("blur", onBlur);
        el.removeEventListener("keydown", onKey);
        const after = norm(el.textContent);
        if (commit) {
          if (after === "" || after === orig) { resetString(key); el.textContent = orig; }
          else if (after !== before) { updateString(key, after, orig || "page", before); }
        } else { el.textContent = before; }
        applying.current = false;
      };
      const onBlur = () => finish(true);
      const onKey = (ev) => {
        if (ev.key === "Enter") { ev.preventDefault(); el.blur(); }
        else if (ev.key === "Escape") { ev.preventDefault(); finish(false); }
      };
      el.addEventListener("blur", onBlur);
      el.addEventListener("keydown", onKey);
    };

    apply();
    const obs = new MutationObserver(() => { if (!applying.current) window.requestAnimationFrame(apply); });
    obs.observe(document.body, { childList: true, subtree: true, characterData: true });
    document.addEventListener("click", onClick, true);
    const onHash = () => setTimeout(apply, 60);
    window.addEventListener("hashchange", onHash);
    document.body.classList.add("live-edit-on");

    return () => {
      obs.disconnect();
      document.removeEventListener("click", onClick, true);
      window.removeEventListener("hashchange", onHash);
      document.body.classList.remove("live-edit-on");
      document.querySelectorAll(".live-editable").forEach((el) => el.classList.remove("live-editable"));
    };
  }, [active, overrides]);

  return null;
}
